import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Loader2, CheckCircle2, Instagram } from "lucide-react";
import { ASSETS } from "../lib/assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = ({ config }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent. Talk soon!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 border-t border-white/10 bg-[#070707]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.4em] text-[#C4F542] mb-6">/ 06 — Let's Connect</p>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-none mb-8">
              LET'S BUILD<br />SOMETHING <span className="text-[#C4F542]">TOGETHER.</span>
            </h2>
            <p className="text-base text-zinc-400 font-body leading-relaxed mb-10">
              Whether you're interested in enrolling a child, becoming a volunteer, partnering with
              us, or supporting our mission, we'd love to connect with you.
            </p>

            <div className="space-y-5">
              <a
                href={`mailto:${config?.contact_email || "connect@ourhovse.org"}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 text-white hover:text-[#C4F542] transition-colors group"
              >
                <span className="h-12 w-12 border border-white/15 flex items-center justify-center group-hover:border-[#C4F542]">
                  <Mail size={18} />
                </span>
                <span className="font-body">{config?.contact_email || "connect@ourhovse.org"}</span>
              </a>
              <div className="flex items-center gap-4 text-zinc-300">
                <span className="h-12 w-12 border border-white/15 flex items-center justify-center">
                  <MapPin size={18} />
                </span>
                <span className="font-body">{config?.org_location || "1635 Lee Road, Cleveland Heights, Ohio 44118"}</span>
              </div>
            </div>

            <div className="mt-10 flex items-center gap-3">
              {[
                { Icon: Instagram, label: "instagram", href: "https://www.instagram.com/ourhovse/" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid={`social-${label}`}
                  className="h-11 w-11 border border-white/15 flex items-center justify-center text-zinc-300 hover:border-[#C4F542] hover:text-[#C4F542] transition-colors"
                  aria-label={label}
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div data-testid="contact-success" className="bg-[#111111] border border-[#C4F542]/40 p-10 lg:p-12 flex flex-col items-center text-center h-full justify-center">
                <CheckCircle2 size={56} className="text-[#C4F542] mb-6" />
                <h3 className="font-display text-4xl text-white mb-3">MESSAGE SENT.</h3>
                <p className="text-zinc-400 font-body max-w-md">
                  Thanks for reaching out. We'll get back to you within a few days.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setDone(false)}
                  className="mt-8 border border-white/30 text-white text-xs uppercase tracking-widest py-3 px-6 hover:border-[#C4F542] hover:text-[#C4F542] transition-colors"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={onSubmit}
                className="bg-[#111111] border border-white/10 p-6 md:p-10 space-y-5"
              >
                <Field
                  label="Your Name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  testId="contact-name"
                  required
                />
                <Field
                  label="Email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  testId="contact-email"
                  required
                />
                <div className="flex flex-col gap-2">
                  <label className="text-xs uppercase tracking-[0.25em] text-zinc-400">Message</label>
                  <textarea
                    data-testid="contact-message"
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={6}
                    required
                    className="bg-[#1A1A1A] border border-white/15 text-white px-4 py-3 focus:outline-none focus:border-[#C4F542] font-body resize-none"
                    placeholder="Tell us what's on your mind..."
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#C4F542] text-black font-bold uppercase tracking-widest text-sm py-5 px-8 hover:bg-white transition-colors duration-300 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = ({ config }) => (
  <footer data-testid="site-footer" className="border-t border-white/10 bg-black">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid md:grid-cols-3 gap-8 items-center">
      <div className="flex items-center gap-3">
        <img src={ASSETS.logo} alt="Our Hovse Foundation" className="h-12 w-12 object-contain" />
        <div>
          <p className="font-display text-xl text-white leading-none">OUR HOVSE</p>
          <p className="font-display text-xl text-[#C4F542] leading-none">FOUNDATION</p>
        </div>
      </div>
      <p className="text-sm text-zinc-500 font-body text-center">
        © {new Date().getFullYear()} {config?.org_name || "Our Hovse Foundation"} · 501(c)(3) nonprofit. All rights reserved.
      </p>
      <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 font-body text-center md:text-right">
        Move · Train · Build
      </p>
    </div>
  </footer>
);

const Field = ({ label, testId, ...rest }) => (
  <div className="flex flex-col gap-2">
    <label className="text-xs uppercase tracking-[0.25em] text-zinc-400">{label}</label>
    <input
      data-testid={testId}
      {...rest}
      className="bg-[#1A1A1A] border border-white/15 text-white px-4 py-3 focus:outline-none focus:border-[#C4F542] font-body"
    />
  </div>
);
